import { CAPABILITY_IDS } from '../capabilities-registry';
import { AutomationRulesHandler } from './automation-rules-handler';
import { BulkUpdateServiceHandler } from './bulk-update-service-handler';
import { BulkUpdateShipFromHandler } from './bulk-update-ship-from-handler';
import { BulkUpdateWeightHandler } from './bulk-update-weight-handler';
import { NavigationHandler } from './navigation-handler';
import { OrderSearchHandler } from './order-search-handler';
import { PackingSlipHandler } from './packing-slip-handler';

/**
 * @typedef {import('./automation-rules-handler').AutomationRuleData} AutomationRuleData
 */

/**
 * @typedef {object} CapabilityResult
 * @property {boolean} success - Whether the capability was executed successfully
 * @property {string} [error] - Error message if failed
 * @property {string} [action] - Action type (navigate, execute, update-automation-rule, etc.)
 * @property {string} [route] - Route to navigate to
 * @property {string | null} [highlightSelector] - CSS selector to highlight
 * @property {boolean} [isFallbackRoute] - Whether fallback route was used
 * @property {object} [routeState] - State to pass via React Router's Link state prop
 * @property {object} [data] - Additional data returned by the capability
 * @property {AutomationRuleData} [automationRuleData] - Automation rule data for rule updates
 * @property {() => void} [executeBeforeNavigate] - Side effects to run when the user clicks the navigation link
 * @property {{ data: object, target: string, commandId?: string } | null} [pendingPrefill] - Data to dispatch to the shippy Redux store before navigation
 */

/**
 * @typedef {object} CapabilityContext
 * @property {string} [currentRoute] - Current route path for context-aware actions
 */

/**
 * Registry of React component handlers.
 * Each component owns its own UI rendering, validation, and user interactions.
 */
const handlerComponents = {
  [CAPABILITY_IDS.NAVIGATION]: NavigationHandler,
  [CAPABILITY_IDS.ORDER_SEARCH]: OrderSearchHandler,
  [CAPABILITY_IDS.PACKING_SLIP]: PackingSlipHandler,
  [CAPABILITY_IDS.AUTOMATION_RULES]: AutomationRulesHandler,
  [CAPABILITY_IDS.BULK_UPDATE_SERVICE]: BulkUpdateServiceHandler,
  [CAPABILITY_IDS.BULK_UPDATE_SHIP_FROM]: BulkUpdateShipFromHandler,
  [CAPABILITY_IDS.BULK_UPDATE_WEIGHT]: BulkUpdateWeightHandler,
};

/**
 * Get the React component handler for a capability (if it has one)
 * @param {string} capabilityId - ID of the capability
 * @returns {React.ComponentType<{parameters: object, conversationId: string, turnNumber: number}> | null} Handler component or null
 */
export const getHandlerComponent = (capabilityId) => {
  return handlerComponents[capabilityId] || null;
};
